// NOTE: USING INLINE STYLES BECAUSE TAILWIND CSS DOES NOT SUPPORT DYNAMIC VALUES FROM PROPS. THE FOLLOWING LINK EXPLAINS WHY:
//https://v2.tailwindcss.com/docs/just-in-time-mode#arbitrary-value-support
import React from 'react';
import Webcam from 'react-webcam';
import { TeleprompterText } from './TeleprompterText';
import { SpeedController } from './SpeedController';
import { FontSizeController } from './FontSizeController';
import { OpacityController } from './OpacityController';
import { TeleprompterModeModal } from './TeleprompterModeModal';
import { TeleprompterHelpModal } from './TeleprompterHelpModal';
import { SlideNavigation } from './SlideNavigation';
import { uploadFile } from '../../data/uploadFile';
import { FileUpload, WebCamComponentProps, TeleprompterMode, Chart } from '@usacsmideo/types';
import { Button, ButtonGreen, ButtonRed } from '../buttons/';
import { useOrientation } from '@uidotdev/usehooks';
import useChart from '../../hooks/useChart';
import { useAuth } from '../../contexts/AuthContext';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import * as Sentry from '@sentry/browser';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../constants/firebaseModular';
import { APPLICATION_STATUS, createLastUpdated, UPDATE_SOURCE } from '../../utilities/updateHelpers';

const PORTRAIT_ASPECT_RATIO = 3 / 4;

export const WebCamVideo = (props: WebCamComponentProps) => {
  const { patientId, setNextEnabled } = props;
  const orientation = useOrientation();
  const { currentUser } = useAuth();
  const userId = currentUser?.uid || patientId || '';
  const chartQuery = useChart(userId);
  const chartData = chartQuery.data as Chart;
  const deviceDetection = useDeviceDetection();
  const webcamRef = React.useRef<Webcam>(null);
  const mediaRecorderRef = React.useRef<MediaRecorder>();
  const dataAvailableHandlerRef = React.useRef<((_event: BlobEvent) => void) | null>(null);
  const stopHandlerRef = React.useRef<(() => void) | null>(null);
  const videoContainerRef = React.useRef<HTMLDivElement>(null);
  const [capturing, setCapturing] = React.useState(false);
  const [reviewing, setReviewing] = React.useState(false);
  const [isScrolling, setIsScrolling] = React.useState(false);
  const [speed, setSpeed] = React.useState(75);
  const [textSize, setTextSize] = React.useState(20);
  const [opacity, setOpacity] = React.useState(0.65);
  const [reviewVideoSrc, setReviewVideoSrc] = React.useState<string | undefined>();
  const [recordedChunks, setRecordedChunks] = React.useState<Array<Blob>>([]);
  const [isUploading, setIsUploading] = React.useState<boolean>(false);
  const [previewNeeded, setPreviewNeeded] = React.useState<boolean>(true);
  const [ready, setReady] = React.useState<boolean>(false);
  const [mode, setMode] = React.useState<TeleprompterMode>('auto-scroll');
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [showModeModal, setShowModeModal] = React.useState(false);
  const [showHelpModal, setShowHelpModal] = React.useState(false);
  const [uploadSuccessful, setUploadSuccessful] = React.useState(false);
  const [mobileScrolling, setMobileScrolling] = React.useState<'up' | 'down' | null>(null);
  const mobileScrollTimeout = React.useRef<NodeJS.Timeout>();
  const mobileScrollInterval = React.useRef<NodeJS.Timeout>();

  // Calculate total slides for slide-advance mode
  const totalSlides = React.useMemo(() => {
    if (!chartData?.SCRIPT_REVIEW?.telePrompterScript) return 0;
    return chartData.SCRIPT_REVIEW.telePrompterScript.split('\n').filter((line) => line.trim().length > 0).length;
  }, [chartData?.SCRIPT_REVIEW?.telePrompterScript]);

  // Platform-specific slider ranges
  const speedMin = deviceDetection.isDesktopOrTablet ? 5 : 11; // THE MINIMUM TIME TO COMPLETE THE SCROLL IN SECONDS
  const speedMax = deviceDetection.isDesktopOrTablet ? 120 : 100; // THE MAXIMUM TIME TO COMPLETE THE SCROLL IN SECONDS
  const textSizeMax = deviceDetection.isDesktopOrTablet ? 66 : 37; // Desktop/Tablet: 10% increase; Mobile: 15% increase

  // SHOW UI AFTER BRIEF DELAY TO ALLOW CAMERA REF TO INITIALIZE
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setReady(true);
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  // Load mode preference from localStorage
  React.useEffect(() => {
    const savedMode = localStorage.getItem('teleprompterMode');
    if (savedMode && (savedMode === 'auto-scroll' || savedMode === 'manual-scroll' || savedMode === 'slide-advance')) {
      setMode(savedMode as TeleprompterMode);
    }
  }, []);

  // Save mode preference to localStorage
  React.useEffect(() => {
    localStorage.setItem('teleprompterMode', mode);
  }, [mode]);

  // Show help modal on first visit
  React.useEffect(() => {
    const hasSeenHelp = localStorage.getItem('hasSeenTeleprompterHelp');
    if (!hasSeenHelp) {
      setShowHelpModal(true);
      localStorage.setItem('hasSeenTeleprompterHelp', 'true');
    }
  }, []);

  // Reset to first slide when mode changes to slide-advance
  React.useEffect(() => {
    if (mode === 'slide-advance') {
      setCurrentSlide(0);
    }
  }, [mode]);

  // Cleanup mobile scroll timeout and interval on unmount
  React.useEffect(() => {
    return () => {
      if (mobileScrollTimeout.current) {
        clearTimeout(mobileScrollTimeout.current);
      }
      if (mobileScrollInterval.current) {
        clearInterval(mobileScrollInterval.current);
      }
    };
  }, []);

  // Mobile scroll button handlers
  const handleMobileScrollStart = (direction: 'up' | 'down') => {
    // Clear any existing timeout/interval first
    if (mobileScrollTimeout.current) {
      clearTimeout(mobileScrollTimeout.current);
    }
    if (mobileScrollInterval.current) {
      clearInterval(mobileScrollInterval.current);
    }

    setMobileScrolling(direction);
    // Trigger scroll event that TeleprompterText can respond to
    window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { direction, action: 'start' } }));

    // Start continuous scrolling after 300ms hold
    mobileScrollTimeout.current = setTimeout(() => {
      mobileScrollInterval.current = setInterval(() => {
        window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { direction, action: 'continue' } }));
      }, 50);
    }, 300);
  };

  const handleMobileScrollEnd = () => {
    setMobileScrolling(null);
    if (mobileScrollTimeout.current) {
      clearTimeout(mobileScrollTimeout.current);
    }
    if (mobileScrollInterval.current) {
      clearInterval(mobileScrollInterval.current);
    }
    window.dispatchEvent(new CustomEvent('mobileScroll', { detail: { action: 'stop' } }));
  };

  // RENDER CONTROL BUTTONS BASED ON CURRENT STATE
  const renderControlButtons = () => {
    // UPLOAD SUCCESSFUL - NO BUTTONS (Next button handled elsewhere)
    if (uploadSuccessful) {
      return null;
    }

    // RECORDING IN PROGRESS
    if (capturing) {
      return (
        <div className="flex flex-row justify-center">
          <Button
            type="button"
            id="stop-recording-button"
            onClick={handleStopCaptureClick}
            textContent="Stop Recording"
          />
        </div>
      );
    }

    // RECORDED VIDEO - REVIEWING
    if (recordedChunks.length > 0 && reviewing) {
      return (
        <div className="flex flex-row justify-evenly">
          <Button type="button" id="upload-button" onClick={handleUpload} textContent="Upload" />
          <ButtonRed type="button" id="re-record-button" onClick={handleReRecord} textContent="Re-Record" />
        </div>
      );
    }

    // RECORDED VIDEO - NOT YET REVIEWING
    if (recordedChunks.length > 0) {
      return (
        <div className="flex flex-col items-center">
          <p className="text-xl font-bold">Click the green button to review your video and submit.</p>
          <div className="flex flex-row justify-evenly">
            <ButtonGreen
              type="button"
              id="review-button"
              onClick={() => setReviewing(true)}
              textContent="Review & Upload"
            />
            <ButtonRed type="button" id="re-record-button" onClick={handleReRecord} textContent="Re-Record" />
          </div>
        </div>
      );
    }

    // NO RECORDING YET
    // For auto-scroll: Need preview first
    if (mode === 'auto-scroll' && previewNeeded) {
      return (
        <div className="flex flex-row items-center justify-center">
          <Button
            type="button"
            id="preview-button"
            onClick={() => {
              setIsScrolling(true);
              setPreviewNeeded(false);
            }}
            textContent="Preview Scroll"
          />
        </div>
      );
    }

    // Auto-scroll: PREVIEW IN PROGRESS
    if (mode === 'auto-scroll' && isScrolling) {
      return (
        <div className="flex flex-row justify-evenly">
          <Button
            type="button"
            id="preview-button"
            onClick={() => {
              setIsScrolling(false);
              setPreviewNeeded(false);
            }}
            textContent="Stop Preview"
          />
          <Button type="button" id="start-recording-button" onClick={handleStartCaptureClick} textContent="Record" />
        </div>
      );
    }

    // Auto-scroll: PREVIEW STOPPED - READY TO RECORD
    if (mode === 'auto-scroll') {
      return (
        <div className="flex flex-row justify-evenly">
          <Button
            type="button"
            id="preview-button"
            onClick={() => {
              setIsScrolling(true);
              setPreviewNeeded(false);
            }}
            textContent="Preview Scroll"
          />
          <Button type="button" id="start-recording-button" onClick={handleStartCaptureClick} textContent="Record" />
        </div>
      );
    }

    // Manual-scroll and Slide-advance: Just show Record button
    return (
      <div className="flex flex-row justify-center">
        <Button type="button" id="start-recording-button" onClick={handleStartCaptureClick} textContent="Record" />
      </div>
    );
  };

  React.useEffect(() => {
    setPreviewNeeded(true);
  }, [speed, textSize, opacity]);

  // CLEANUP BLOB URL TO PREVENT MEMORY LEAKS
  React.useEffect(() => {
    return () => {
      if (reviewVideoSrc) {
        URL.revokeObjectURL(reviewVideoSrc);
      }
    };
  }, [reviewVideoSrc]);

  // CLEANUP MEDIA RECORDER ON UNMOUNT
  React.useEffect(() => {
    return () => {
      if (mediaRecorderRef.current) {
        if (dataAvailableHandlerRef.current) {
          mediaRecorderRef.current.removeEventListener('dataavailable', dataAvailableHandlerRef.current);
        }
        if (stopHandlerRef.current) {
          mediaRecorderRef.current.removeEventListener('stop', stopHandlerRef.current);
        }
        if (mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      }
    };
  }, []);

  const videoConstraints = React.useMemo(
    () => ({
      // Don't constrain aspect ratio - let camera provide its natural output
      // We'll crop to portrait in CSS using object-fit: cover
      facingMode: 'user',
    }),
    [],
  );

  // RELOAD THE COMPONENT ON ORIENTATION CHANGE
  React.useEffect(() => {
    // STOP ANY ACTIVE RECORDING WHEN ORIENTATION CHANGES
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.warn('Stopping recording due to orientation change');
      if (dataAvailableHandlerRef.current) {
        mediaRecorderRef.current.removeEventListener('dataavailable', dataAvailableHandlerRef.current);
      }
      if (stopHandlerRef.current) {
        mediaRecorderRef.current.removeEventListener('stop', stopHandlerRef.current);
      }
      mediaRecorderRef.current.stop();
      setCapturing(false);
      setIsScrolling(false);
    }

    // The Webcam component handles its own camera access via key={orientation.type}
    // No need to manually request/release camera here
  }, [orientation]);

  const handleStartCaptureClick = React.useCallback(() => {
    setCapturing(true);
    // Only auto-start scrolling for auto-scroll mode
    if (mode === 'auto-scroll') {
      setIsScrolling(true);
    }
    // Clear any previous recording chunks
    setRecordedChunks([]);

    // Clean up previous recorder and listeners if they exist
    if (mediaRecorderRef.current) {
      if (dataAvailableHandlerRef.current) {
        mediaRecorderRef.current.removeEventListener('dataavailable', dataAvailableHandlerRef.current);
      }
      if (stopHandlerRef.current) {
        mediaRecorderRef.current.removeEventListener('stop', stopHandlerRef.current);
      }
    }

    // Store handlers in refs so we can remove them later
    const handleDataAvailable = (blob: BlobEvent) => {
      const { data } = blob;
      if (data.size > 0) {
        console.log(`Received chunk: ${data.size} bytes, recorder state: ${mediaRecorderRef.current?.state}`);
        // Accumulate all chunks (don't create blob URL here - side effects in state updater)
        setRecordedChunks((prev) => prev.concat(data));
      } else {
        console.warn('No data available in blob event', data);
      }
    };
    dataAvailableHandlerRef.current = handleDataAvailable;

    const handleStop = () => {
      console.log('Stop event fired');
      // Create blob URL after a small delay to ensure all chunks are in state
      setTimeout(() => {
        setRecordedChunks((chunks) => {
          if (chunks.length > 0) {
            console.log(
              `Creating blob from ${chunks.length} chunks, total size: ${chunks.reduce((sum, chunk) => sum + chunk.size, 0)} bytes`,
            );
            const completeBlob = new Blob(chunks, { type: chunks[0].type });
            setReviewVideoSrc((prevSrc) => {
              if (prevSrc) {
                URL.revokeObjectURL(prevSrc);
              }
              return URL.createObjectURL(completeBlob);
            });
          }
          return chunks;
        });
      }, 100);
    };
    stopHandlerRef.current = handleStop;

    if (webcamRef.current?.stream) {
      mediaRecorderRef.current = new MediaRecorder(webcamRef.current.stream, {
        // mimeType: "video/webm",  IGNORING FOR CROSS BROWSER SUPPORT
      });

      // Accumulate chunks as they become available
      mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);

      // Create blob URL from all chunks when recording stops
      mediaRecorderRef.current.addEventListener('stop', handleStop);

      // Request chunks every 1 second (timeslice) to prevent Safari from keeping everything in memory
      // This fixes truncation issues with longer recordings in Safari
      mediaRecorderRef.current.start(1000);
    }
  }, [mode]);

  const handleStopCaptureClick = React.useCallback(() => {
    if (mediaRecorderRef.current) {
      // Stop the recorder - this will trigger a final 'dataavailable' event with recorded data
      // Don't remove the listener here, as we need to receive the final data
      mediaRecorderRef.current.stop();
    }
    setCapturing(false);
    setIsScrolling(false);
  }, []);

  const handleReRecord = React.useCallback(() => {
    if (reviewVideoSrc) {
      URL.revokeObjectURL(reviewVideoSrc);
    }
    setIsScrolling(false);
    setCapturing(false);
    setReviewing(false);
    setRecordedChunks([]);
    setReviewVideoSrc(undefined);
  }, [reviewVideoSrc]);

  const handleUserMediaError = async (error: string | DOMException) => {
    console.error('Error opening webcam:', error);

    // EXTRACT ERROR NAME FROM EITHER STRING OR DOMEXCEPTION
    const errorName = typeof error === 'string' ? error : error.name;

    // CAPTURE ERROR IN SENTRY FOR DEBUGGING
    Sentry.captureException(error);

    // PROVIDE CONTEXT-SPECIFIC ERROR MESSAGES BASED ON ERROR TYPE
    if (errorName.includes('NotAllowedError')) {
      // USER DENIED CAMERA/MICROPHONE PERMISSION
      alert(
        'Camera and microphone access denied. To record your video:\n\n' +
          '1. Click the camera icon in your browser address bar\n' +
          '2. Select "Allow" for camera and microphone\n' +
          '3. Refresh this page\n\n' +
          'If you continue having issues, try using a different browser or device.',
      );
    } else if (errorName.includes('NotFoundError')) {
      // NO CAMERA HARDWARE DETECTED
      alert(
        'No camera detected on this device.\n\n' +
          'Please ensure:\n' +
          '• Your device has a working camera\n' +
          '• The camera is not being used by another application\n' +
          '• Camera drivers are properly installed\n\n' +
          'If this is a mobile device, try using the rear camera. Otherwise, please try a different device with a camera.',
      );
    } else if (errorName.includes('NotReadableError')) {
      // CAMERA HARDWARE ERROR OR IN USE BY ANOTHER APP
      alert(
        'Unable to access your camera. This usually happens when:\n\n' +
          '• Another application is using the camera\n' +
          '• The camera hardware is malfunctioning\n' +
          '• Browser permissions are restricted\n\n' +
          'To fix this:\n' +
          '1. Close other apps that might be using the camera (Zoom, Teams, etc.)\n' +
          '2. Refresh this page and wait 30 seconds before clicking Preview Scroll\n' +
          '3. If the issue persists, restart your device\n\n' +
          'IMPORTANT: Do not click Record if your video preview appears frozen.',
      );
    } else if (errorName.includes('OverconstrainedError') || errorName.includes('ConstraintNotSatisfiedError')) {
      // CAMERA DOESN'T SUPPORT REQUESTED SETTINGS
      alert(
        'Your camera does not support the required video settings.\n\n' +
          'This may be due to:\n' +
          '• Outdated camera drivers\n' +
          '• Limited camera capabilities\n' +
          '• Browser compatibility issues\n\n' +
          'Please try using a different device or updating your camera drivers.',
      );
    } else {
      // UNKNOWN ERROR
      alert(
        'An unexpected error occurred while accessing your camera.\n\n' +
          'Error details: ' +
          errorName +
          '\n\n' +
          'Please try:\n' +
          '1. Refreshing the page\n' +
          '2. Using a different browser (Chrome or Safari recommended)\n' +
          '3. Restarting your device\n\n' +
          'If the problem continues, please contact support.',
      );
    }
  };

  const handleUpload = React.useCallback(() => {
    setIsUploading(true);
    if (recordedChunks.length) {
      let type = recordedChunks[0].type;
      // TODO: ENABLE THE LOG BELOW & CHECK HERE FIRST FOR NEW CODEC IF TRANSCODER FAILS ON CERTAIN BROWSERS
      // console.warn('Patient Portal uploading Video of type:', type);
      // HANDLE PIXEL CHROME BROWSER ISSUE
      if (type === 'video/x-matroska;codecs=avc1,opus') {
        type = 'video/webm';
      }
      const blob = new Blob(recordedChunks, {
        type: type,
      });
      const payload = {
        bucket: 'video',
        path: `${patientId}/originalVideo`,
        file: blob,
      } as FileUpload;

      const uploadTask = uploadFile(payload);
      if (!uploadTask) {
        Sentry.captureMessage('Unable to create upload task');
        console.error('Unable to create upload task');
        setIsUploading(false);
        alert(
          'Unable to connect to upload server. This is likely due to your current network quality or configuration. Please try again from your personal network without a VPN. If this problem persists, please notify support.',
        );
        return;
      }
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          console.info('Upload is ' + progress + '% done');
        },
        (error) => {
          Sentry.captureException(error);
          console.error(error);
          setIsUploading(false);
          alert(`Error uploading file: ${error.message}`);
        },
        async () => {
          setIsUploading(false);
          setUploadSuccessful(true);
          if (setNextEnabled) {
            setNextEnabled(true);
          }

          // UPDATE lastUpdated ON THE USER DOCUMENT
          try {
            const userDocRef = doc(db, 'users', patientId);
            const lastUpdated = createLastUpdated(UPDATE_SOURCE.PORTAL, patientId);
            await setDoc(userDocRef, { lastUpdated, applicationStatus: APPLICATION_STATUS.OPEN }, { merge: true });
          } catch (error) {
            console.error('[WebCamVideo] Failed to update lastUpdated:', error);
            Sentry.captureException(error);
            // DON'T ALERT - VIDEO UPLOAD SUCCEEDED, lastUpdated IS SECONDARY
          }

          alert('Upload successful. You may now click Next at the bottom of this page.');
        },
      );

      setRecordedChunks([]);
    }
  }, [recordedChunks, patientId, setNextEnabled]);

  // Detect phone in landscape orientation (should show rotate message)
  const isPhoneLandscape = React.useMemo(() => {
    return deviceDetection.isPhone && orientation.type?.includes('landscape');
  }, [deviceDetection.isPhone, orientation.type]);

  // Detect if tablet in landscape (iPad, Android tablets)
  const isTabletLandscape = React.useMemo(() => {
    return deviceDetection.isTablet && orientation.type?.includes('landscape');
  }, [deviceDetection.isTablet, orientation.type]);

  if (isUploading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        <div role="status">
          <svg
            aria-hidden="true"
            className="h-64 w-64 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span className="sr-only">Loading...</span>
        </div>
        <div>Please wait while we upload your video</div>
      </div>
    );
  }

  if (!ready) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex w-full flex-col items-center">
      {/* ROTATE DEVICE OVERLAY FOR PHONES IN LANDSCAPE */}
      {isPhoneLandscape && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#730D0E] px-8 text-center text-white">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mb-6 h-24 w-24"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          <h2 className="mb-4 text-2xl font-bold">Please Rotate Your Device</h2>
          <p className="text-lg">
            For the best teleprompter experience, please rotate your phone to portrait orientation. If needed, disable
            screen orientation lock in your device settings.
          </p>
        </div>
      )}

      <div
        id="webcam-video-component"
        className="flex w-full flex-col items-center"
        style={{
          maxWidth: '90vw',
        }}
      >
        {/* Settings and Help group - rendered above the camera output.
            Hidden during review and while capturing. */}
        {!reviewing && !capturing && (
          <div className="mb-4 flex items-center justify-center gap-3">
            <button
              onClick={() => setShowModeModal(true)}
              className="flex h-12 items-center justify-center rounded-full bg-gray-200 px-5 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-300"
              title="Change display mode"
              aria-label="Settings"
            >
              Settings
            </button>
            <button
              onClick={() => setShowHelpModal(true)}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-[#730D0E]/10 transition-colors hover:bg-[#730D0E]/20"
              title="Help"
              aria-label="Help"
            >
              <span className="text-lg font-bold text-[#730D0E]">?</span>
            </button>
          </div>
        )}

        {/* VIDEO CONTAINER - ALWAYS PORTRAIT ASPECT RATIO */}
        <div
          ref={videoContainerRef}
          className="relative w-full"
          style={{
            aspectRatio: `${PORTRAIT_ASPECT_RATIO}`,
            overflow: 'hidden',
            backgroundColor: '#000',
            margin: '0 auto', // Center when constrained by maxWidth
            // Dynamic max-width based on device and orientation
            maxWidth: isTabletLandscape
              ? 'min(600px, calc(50vh * 3 / 4))'
              : window.innerWidth >= 768
                ? 'min(600px, calc(70vh * 3 / 4))'
                : 'min(600px, calc(45vh * 3 / 4))',
          }}
        >
          {reviewing && reviewVideoSrc ? (
            // REVIEW PLAYBACK
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              src={reviewVideoSrc}
              controls
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                backgroundColor: '#000',
              }}
            />
          ) : (
            // LIVE WEBCAM WITH TELEPROMPTER OVERLAY
            <>
              {/* OPACITY OVERLAY */}
              <div
                id="transparency-overlay"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  backgroundColor: 'white',
                  opacity: opacity,
                  height: '100%',
                  width: '100%',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
              {/* WEBCAM - CROPS LANDSCAPE VIDEO TO PORTRAIT */}
              <Webcam
                key={orientation.type}
                id="webcamView"
                audio={true}
                muted={true}
                ref={webcamRef}
                videoConstraints={videoConstraints}
                onUserMediaError={(err) => handleUserMediaError(err)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover', // Crops center of landscape video to portrait frame
                  display: 'block',
                }}
              />
              {/* TELEPROMPTER TEXT OVERLAY */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  zIndex: 2,
                  overflow: 'hidden',
                  padding: '20px 16px 16px',
                }}
              >
                <TeleprompterText
                  isScrolling={isScrolling}
                  setIsScrolling={setIsScrolling}
                  speed={speed}
                  textSize={textSize}
                  mode={mode}
                  currentSlide={currentSlide}
                  totalSlides={totalSlides}
                  onSlideChange={setCurrentSlide}
                />
              </div>
            </>
          )}
        </div>

        {/* CONTROLS - ALWAYS VISIBLE BELOW VIDEO */}
        <div className="mt-4 flex w-full flex-col items-center gap-4">
          {/* Manual-scroll up/down buttons - live controls, visible even during recording.
              Settings/Help moved above the camera output. */}
          {!reviewing && mode === 'manual-scroll' && (
            <div className="flex items-center justify-center gap-3">
              {/* Up Button */}
              <button
                onTouchStart={() => handleMobileScrollStart('up')}
                onTouchEnd={handleMobileScrollEnd}
                onMouseDown={() => handleMobileScrollStart('up')}
                onMouseUp={handleMobileScrollEnd}
                onMouseLeave={handleMobileScrollEnd}
                className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
                  mobileScrolling === 'up'
                    ? 'bg-[#730D0E] text-white'
                    : 'bg-[#730D0E]/10 text-[#730D0E] hover:bg-[#730D0E]/20'
                }`}
                title="Scroll up"
                aria-label="Scroll up"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>

              {/* Down Button */}
              <button
                onTouchStart={() => handleMobileScrollStart('down')}
                onTouchEnd={handleMobileScrollEnd}
                onMouseDown={() => handleMobileScrollStart('down')}
                onMouseUp={handleMobileScrollEnd}
                onMouseLeave={handleMobileScrollEnd}
                className={`flex h-10 w-10 items-center justify-center rounded-full transition-colors ${
                  mobileScrolling === 'down'
                    ? 'bg-[#730D0E] text-white'
                    : 'bg-[#730D0E]/10 text-[#730D0E] hover:bg-[#730D0E]/20'
                }`}
                title="Scroll down"
                aria-label="Scroll down"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Slide navigation for slide-advance mode */}
          {!reviewing && mode === 'slide-advance' && (
            <SlideNavigation
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              onPrevious={() => setCurrentSlide((prev) => Math.max(0, prev - 1))}
              onNext={() => setCurrentSlide((prev) => Math.min(totalSlides - 1, prev + 1))}
            />
          )}

          {/* Recording buttons — kept above the teleprompter settings sliders */}
          <div className="w-full">{renderControlButtons()}</div>

          {/* Sliders */}
          {!reviewing && (
            <div className="w-full px-6">
              <FontSizeController
                textSize={textSize}
                setTextSize={setTextSize}
                scrolling={isScrolling}
                max={textSizeMax}
              />
              {(mode === 'auto-scroll' || mode === 'manual-scroll') && (
                <SpeedController
                  speed={speed}
                  setSpeed={setSpeed}
                  scrolling={isScrolling}
                  min={speedMin}
                  max={speedMax}
                />
              )}
              <OpacityController opacity={opacity} setOpacity={setOpacity} scrolling={isScrolling} />
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <TeleprompterModeModal
        isOpen={showModeModal}
        onClose={() => setShowModeModal(false)}
        currentMode={mode}
        onModeChange={(newMode) => {
          setMode(newMode);
          setPreviewNeeded(true);
          setIsScrolling(false);
        }}
      />
      <TeleprompterHelpModal isOpen={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </div>
  );
};
